/**
 * UpdateBanner Component
 *
 * GW-2028: Non-intrusive notification shown when a new app version is deployed.
 * Uses role="status" with aria-live="polite" for accessible announcement.
 *
 * @see PreviewBanner for pattern reference
 */

import { Button, cn } from '@gw/ui';
import { RefreshCw, X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface UpdateBannerProps {
  showBanner: boolean;
  onDismiss: () => void;
  onRefresh: () => void;
  className?: string;
}

export function UpdateBanner({ showBanner, onDismiss, onRefresh, className }: UpdateBannerProps) {
  if (!showBanner || typeof document === 'undefined') {
    return null;
  }

  // GW-10338: Portal to document.body so the banner escapes any ancestor stacking
  // context and becomes a sibling of the Radix Sheet marketplace drawer (also
  // portalled to body at z-50). Combined with the reserved top tier (z-[9999]),
  // this guarantees the update banner paints above the drawer overlay.
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-label="Application update available"
      className={cn(
        'fixed top-0 right-0 left-0 z-[9999] pointer-events-auto',
        'bg-primary text-primary-foreground',
        'px-4 py-2',
        'flex items-center justify-center gap-3',
        className
      )}
    >
      <RefreshCw className="h-4 w-4" aria-hidden="true" />
      <span className="text-center text-sm font-medium">
        A new version of General Wisdom is available.
      </span>
      <Button
        variant="secondary"
        size="sm"
        onClick={onRefresh}
        className="h-6 px-3"
        aria-label="Refresh to update"
      >
        Refresh
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onDismiss}
        className="hover:bg-primary-foreground/10 text-primary-foreground ml-2 h-6 px-2"
        aria-label="Dismiss update notification"
      >
        <X className="h-3 w-3" />
      </Button>
    </div>,
    document.body
  );
}
